/* eslint-disable react-hooks/exhaustive-deps */
import React, { SyntheticEvent } from "react";
import {
    Autocomplete,
    Box,
    CircularProgress,
    Stack,
} from "@mui/material";
import { debounce } from "lodash";
import { CustomTextField } from "../../../../Components/formComponents";
import { axios_api } from "../../../../api/axios-api";
import { ICategory, defaultCategory } from "../../../../redux/inventory";
import { GET_CATEGORIES_BASE_URL } from "../../../../api/axios-api.endpoints";
import { useOutletContext } from "../../../../providers";

export interface ICategoryAutoSearchProps {
    label?: string;
    name: string;
    value: {
        category_uuid: string,
        category_name: string,
    };
    onSelect: (data: ICategory) => void;
    disabled?: boolean;
    error?: string;
}

const INITIAL_STATE: ICategory = defaultCategory;

export const CategoryAutoSearch: React.FC<ICategoryAutoSearchProps> = ({
    label, name, value, disabled, error, onSelect
}) => {
 const { outlet_details: { outlet_uuid } } = useOutletContext()
    const [options, setOptions] = React.useState<readonly ICategory[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearchText] = React.useState<string>("");

    const fetchSuggestion = async (enteredValue: string) => {
        setLoading(true);
        try {
            let searchQuery = `outlet_uuid=${outlet_uuid}`;
            if (enteredValue.length > 0) {
                searchQuery += `columns=category_name&value=${enteredValue}&`;
            }
            const res = await axios_api.get(
                `${GET_CATEGORIES_BASE_URL}?${searchQuery}`,
            );
            const finalData: ICategory[] = res.data.data;
            if (value?.category_uuid && value?.category_uuid?.length > 0 && search.length === 0) {
                const option: ICategory = {
                    ...INITIAL_STATE,
                    category_uuid: value.category_uuid,
                    category_name: value.category_name,
                };
                finalData.push(option);
            }
            setOptions(finalData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };
    const debounceFn = React.useCallback(debounce(fetchSuggestion, 800), []);

    const getOptionLabel = (option: ICategory | string) => {
        if (typeof option === "string") return option || "";
        return option.category_name || ""
    };

    const getValue = () => {
        return (
            options.find((option) => option.category_uuid === value.category_uuid) ||
            null
        );
    };

    const handleDropDownOnFocus = () => {
        debounceFn("");
    };

    React.useEffect(() => {
        if (search && search.length > 0 && search !== value.category_name) {
            debounceFn(search);
        }
    }, [search]);

    React.useEffect(() => {
        if (value.category_uuid && value.category_uuid.length > 0) {
            const option: ICategory = {
                ...INITIAL_STATE,
                category_uuid: value.category_uuid,
                category_name: value.category_name,
            };
            setOptions([option])
        } else {
            setOptions([]);
        }
    }, [value]);

    return (
        <>
            <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
                <Autocomplete
                    disablePortal
                    fullWidth
                    freeSolo
                    size="small"
                    disabled={disabled}
                    sx={{
                        minWidth: 180,
                        ".MuiOutlinedInput-root": {
                            paddingTop: "2px",
                            paddingBottom: "2px",
                            fontSize: "0.8rem",
                            color: "rgb(38, 38, 38)",
                            width: "100%",
                            backgroundColor: disabled ? "#f8f9fb" : "inherit",
                        },
                    }}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                        typeof option !== "string" && typeof value !== "string"
                            ? option.category_name === value.category_name
                            : option === value
                    }
                    filterOptions={(x) => x}
                    options={options}
                    autoComplete
                    includeInputInList
                    value={getValue()}
                    noOptionsText="No Category Found"
                    onFocus={handleDropDownOnFocus}
                    onChange={(
                        event: SyntheticEvent<Element, Event>,
                        newValue: string | ICategory | null,
                    ) => {
                        if (newValue && typeof newValue !== "string") {
                            onSelect(newValue);
                        }
                    }}
                    onInputChange={(event, newInputValue) => {
                        if ((event && event.type === "change") || !newInputValue) {
                            setSearchText(newInputValue);
                        }
                    }}
                    renderInput={(params) => (
                        <CustomTextField
                            name={name}
                            {...params}
                            fullWidth
                            label={label}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading && <CircularProgress color="inherit" size={20} />}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                            disabled={disabled}
                            error={error ? true : false}
                            helperText={error}
                        />
                    )}
                />
            </Stack>
        </>
    );
};
